import { DateTime } from 'luxon'
import type { TScenarioPath } from '../components/goals/creation/shared/scenario'
import type { TGoal } from '../components/goals/goalTypes'
import { DEFAULT_FOLDER_ID } from '../constants'
import type {
    TCalendarAccount,
    TEmailDeliveryStatus,
    TInternalDashboardSummary,
    TInternalEmailTest,
    TInternalFeatureFlags,
    TInternalUsage,
    TInternalUsageWindow,
    TOverviewView,
    TRecurringTaskTemplate,
    TRoutine,
    TSetting,
    TTaskFolder,
    TTaskV4,
    TUserInfo,
} from '../utils/types'

// Shared fixture payloads for local walkthrough mode and Cypress specs.
//
// Several payloads embed each other's ids: the overview list references the task by id, and the
// per-folder sort settings encode the folder id in their `field_key`. Keep those ids in sync; a
// mismatch does not error, it just renders an empty list.
export const FOLDER_ID = DEFAULT_FOLDER_ID
export const TASK_ID = '5f9b3b3b3b3b3b3b3b3b3b02'
export const OVERVIEW_VIEW_ID = '5f9b3b3b3b3b3b3b3b3b3b03'
export const DUE_TODAY_VIEW_ID = '5f9b3b3b3b3b3b3b3b3b3b04'
export const FIRST_SUBTASK_ID = '5f9b3b3b3b3b3b3b3b3b3b05'
export const SECOND_SUBTASK_ID = '5f9b3b3b3b3b3b3b3b3b3b06'
export const MORNING_ROUTINE_ID = '5f9b3b3b3b3b3b3b3b3b3b20'
export const EVENING_ROUTINE_ID = '5f9b3b3b3b3b3b3b3b3b3b21'
export const MORNING_ROUTINE_VIEW_ID = '5f9b3b3b3b3b3b3b3b3b3b22'
export const EVENING_ROUTINE_VIEW_ID = '5f9b3b3b3b3b3b3b3b3b3b23'
export const CALENDAR_ACCOUNT_ID = 'walkthrough@example.com'

export const FOLDER_NAME = 'Walkthrough Tasks'
export const TASK_TITLE = 'Review the walkthrough-ready task'
export const FIRST_SUBTASK_TITLE = 'Confirm the nested row spacing'
export const SECOND_SUBTASK_TITLE = 'Check the responsive alignment'

// The signed-in fixture has paid access; walkthrough modes override these fields to demonstrate
// the active-trial and expired read-only shells without changing the normal walkthrough.
export const userInfo: TUserInfo = {
    agreed_to_terms: true,
    opted_into_marketing: false,
    name: 'John',
    is_employee: false,
    email: 'john@johnglomerate.com',
    subscription_status: 'active',
    is_subscribed: true,
    is_in_trial: false,
    trial_days_remaining: 0,
    has_product_access: true,
    is_read_only: false,
}

// Mirrors the backend registry defaults: routines, recurrence suggestions and the due today email start off; goals and
// research start on.
export const internalFeatureFlags: TInternalFeatureFlags = {
    routines: {
        key: 'routines',
        name: 'Routines',
        description: 'Ordered checklists you can run through each day from the overview.',
        enabled: false,
        updated_at: '',
        opted_in_count: 0,
        enabled_user_count: 0,
    },
    goals: {
        key: 'goals',
        name: 'Goals',
        description: 'Set goals, link tasks and repeats to them, and review progress weekly.',
        enabled: true,
        updated_at: '',
        opted_in_count: 0,
        enabled_user_count: 0,
    },
    research: {
        key: 'research',
        name: 'Research',
        description: 'Run web research from tasks and keep the answer with its cited sources.',
        enabled: true,
        updated_at: '',
        opted_in_count: 0,
        enabled_user_count: 0,
    },
    recurrence_suggestions: {
        key: 'recurrence_suggestions',
        name: 'Recurrence suggestions',
        description: 'Suggest recurrence after similar tasks are created repeatedly.',
        enabled: false,
        updated_at: '',
        opted_in_count: 0,
        enabled_user_count: 0,
    },
    due_today_email: {
        key: 'due_today_email',
        name: 'Due today email',
        description: 'A morning email on days you have tasks due or overdue.',
        enabled: false,
        updated_at: '',
        opted_in_count: 0,
        enabled_user_count: 0,
    },
    calendar_planning: {
        key: 'calendar_planning',
        name: 'Auto-block calendar',
        description: 'Preview a recommended day plan and add it to your calendar after review.',
        enabled: false,
        updated_at: '',
        opted_in_count: 0,
        enabled_user_count: 0,
    },
}

// The state that prompted the email-test banner: a server with no Resend key.
export const internalEmailDelivery: TEmailDeliveryStatus = {
    enabled: false,
    switched_on: true,
    switch_updated_at: '',
    config_issue: 'key_missing',
}

// Mirrors the backend email registry (internalEmailTests).
export const internalEmailTests: TInternalEmailTest[] = [
    {
        key: 'welcome',
        name: 'Welcome',
        description: 'Sent once to new users after signup, from the drip campaign.',
    },
    {
        key: 'subscription_acknowledgment',
        name: 'Subscription acknowledgment',
        description: 'Sent once the first payment is paid, as the record of the subscription terms and the payment.',
    },
    {
        key: 'due_today',
        name: 'Due today',
        description: 'Sent on mornings the user has tasks due or overdue, to users with the Due today email flag.',
    },
]

// A native General Task task, so the task-detail actions menu offers the full non-external set
// (including "Schedule on calendar", which TaskActionsDropdown hides for done/deleted tasks).
export const task: TTaskV4 = {
    id: TASK_ID,
    id_ordering: 1,
    title: TASK_TITLE,
    deeplink: '',
    body: 'Seeded by walkthrough mode so automated review recordings can show the signed-in app.',
    priority_normalized: 0,
    due_date: '',
    source: { name: 'General Task', logo: 'generaltask' },
    sender: '',
    is_done: false,
    is_deleted: false,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
    deleted_at: '',
    completed_at: '',
    id_folder: FOLDER_ID,
    subtask_ids: [],
    recurrence_suggestion: {
        status: 'ready',
        recurrence_rate: 2,
        suggested_date: '2026-10-22',
    },
}

// Generic API stubs and create-task fakes clone `task`, so attach child ids only to the walkthrough
// parent that is returned alongside those child records.
export const taskWithSubtasks: TTaskV4 = {
    ...task,
    subtask_ids: [FIRST_SUBTASK_ID, SECOND_SUBTASK_ID],
}

export const subtasks: TTaskV4[] = [
    {
        ...task,
        id: FIRST_SUBTASK_ID,
        id_ordering: 1,
        id_folder: undefined,
        id_parent: TASK_ID,
        subtask_ids: [],
        title: FIRST_SUBTASK_TITLE,
        body: 'A walkthrough subtask with metadata so row columns are reviewable.',
        due_date: '2026-11-02',
        priority_normalized: 2,
        recurrence_suggestion: undefined,
    },
    {
        ...task,
        id: SECOND_SUBTASK_ID,
        id_ordering: 2,
        id_folder: undefined,
        id_parent: TASK_ID,
        subtask_ids: [],
        title: SECOND_SUBTASK_TITLE,
        body: 'A second walkthrough subtask that makes inconsistent indentation visible.',
        due_date: '2026-11-03',
        priority_normalized: 1,
        recurrence_suggestion: undefined,
    },
]

export const folders: TTaskFolder[] = [
    { id: FOLDER_ID, name: FOLDER_NAME, task_ids: [TASK_ID], is_done: false, is_trash: false },
    { id: '000000000000000000000004', name: 'Done', task_ids: [], is_done: true, is_trash: false },
    { id: '000000000000000000000005', name: 'Trash', task_ids: [], is_done: false, is_trash: true },
]

// `useOverviewLists` does not render `view_items` from this payload for task_section lists. It
// re-derives them by matching `view_item_ids` against the /tasks/v4/ cache, so the id has to appear
// in both or the list renders empty.
export const routines: TRoutine[] = [
    {
        id: MORNING_ROUTINE_ID,
        name: 'Morning routine',
        id_ordering: 1,
        status: 'current',
        remaining_count: 2,
        streak: 4,
        items: [
            { id: '5f9b3b3b3b3b3b3b3b3b3b30', title: 'Open the blinds', id_ordering: 1, is_completed: true },
            { id: '5f9b3b3b3b3b3b3b3b3b3b31', title: 'Make the bed', id_ordering: 2, is_completed: true },
            { id: '5f9b3b3b3b3b3b3b3b3b3b32', title: 'Take vitamins', id_ordering: 3, is_completed: true },
            { id: '5f9b3b3b3b3b3b3b3b3b3b33', title: 'Weigh in', id_ordering: 4, is_completed: true },
            { id: '5f9b3b3b3b3b3b3b3b3b3b34', title: 'Stretch', id_ordering: 5, is_completed: true },
            { id: '5f9b3b3b3b3b3b3b3b3b3b35', title: 'Brew coffee', id_ordering: 6, is_completed: true },
            { id: '5f9b3b3b3b3b3b3b3b3b3b36', title: 'Feed the dog', id_ordering: 7, is_completed: true },
            { id: '5f9b3b3b3b3b3b3b3b3b3b37', title: 'Check the weather', id_ordering: 8, is_completed: true },
            { id: '5f9b3b3b3b3b3b3b3b3b3b38', title: 'Pack lunch', id_ordering: 9, is_completed: true },
            { id: '5f9b3b3b3b3b3b3b3b3b3b39', title: 'Clear the counter', id_ordering: 10, is_completed: true },
            {
                id: '5f9b3b3b3b3b3b3b3b3b3b24',
                title: 'Drink water',
                id_ordering: 11,
                is_completed: false,
            },
            {
                id: '5f9b3b3b3b3b3b3b3b3b3b25',
                title: 'Review today',
                id_ordering: 12,
                is_completed: false,
            },
        ],
    },
    {
        id: EVENING_ROUTINE_ID,
        name: 'Evening routine',
        id_ordering: 2,
        status: 'up_next',
        remaining_count: 2,
        streak: 7,
        items: [
            { id: '5f9b3b3b3b3b3b3b3b3b3b26', title: 'Clear the desk', id_ordering: 1, is_completed: false },
            { id: '5f9b3b3b3b3b3b3b3b3b3b27', title: 'Plan tomorrow', id_ordering: 2, is_completed: false },
        ],
    },
]

export const routineOverviewView = (routine: TRoutine, id: string): TOverviewView => ({
    id,
    name: routine.name,
    type: 'routine',
    routine_id: routine.id,
    is_reorderable: false,
    logo: 'generaltask',
    view_items: routine.items,
    view_item_ids: routine.items.map((item) => item.id),
    sources: [],
    is_linked: true,
    has_tasks_completed_today: routine.status === 'complete',
    status: routine.status,
    remaining_count: routine.remaining_count,
    streak: routine.streak,
})

export const overviewViews: TOverviewView[] = [
    routineOverviewView(routines[0], MORNING_ROUTINE_VIEW_ID),
    {
        id: OVERVIEW_VIEW_ID,
        name: FOLDER_NAME,
        type: 'task_section',
        task_section_id: FOLDER_ID,
        is_reorderable: true,
        logo: 'generaltask',
        view_items: [],
        view_item_ids: [TASK_ID],
        sources: [],
        is_linked: true,
        has_tasks_completed_today: false,
    },
    routineOverviewView(routines[1], EVENING_ROUTINE_VIEW_ID),
    {
        id: DUE_TODAY_VIEW_ID,
        name: 'Due Today',
        type: 'due_today',
        is_reorderable: false,
        logo: 'generaltask',
        view_items: [],
        view_item_ids: [],
        sources: [],
        is_linked: true,
        has_tasks_completed_today: false,
    },
]

// TaskSectionView needs the backend's sort/filter contract metadata to select the settings for this
// folder and page context.
const sortSettings = (groupId: string, suffix: '_main' | '_overview'): TSetting[] =>
    [
        {
            key: 'task_sorting_preference',
            value: 'manual',
            choices: ['manual', 'due_date', 'priority', 'created_at', 'updated_at'],
            role: 'sort' as const,
        },
        {
            key: 'task_sorting_direction',
            value: 'ascending',
            choices: ['descending', 'ascending'],
            role: 'direction' as const,
        },
    ].map(({ key, value, choices, role }) => ({
        field_key: `${groupId}_${key}${suffix}`,
        field_value: value,
        field_name: key,
        choices: choices.map((choice) => ({ choice_key: choice, choice_name: '' })),
        sort_and_filter: { group: 'task', role, scope_id: groupId, context: suffix.slice(1) },
    }))

export const settings: TSetting[] = [
    ...sortSettings(FOLDER_ID, '_main'),
    ...sortSettings(FOLDER_ID, '_overview'),
    {
        field_key: 'calendar_account_id_for_new_tasks',
        field_value: CALENDAR_ACCOUNT_ID,
        field_name: 'Calendar account for new tasks',
        choices: [],
    },
    {
        field_key: 'calendar_plan_start',
        field_value: '09:00',
        field_name: 'Auto-block start',
        choices: [],
    },
    {
        field_key: 'calendar_plan_end',
        field_value: '17:30',
        field_name: 'Auto-block end',
        choices: [],
    },
    {
        field_key: 'calendar_plan_morning',
        field_value: 'false',
        field_name: 'Auto-block every morning',
        choices: [],
    },
    {
        field_key: 'recurring_task_filtering_preference',
        field_value: 'no_deleted',
        field_name: 'recurring_task_filtering_preference',
        sort_and_filter: { group: 'recurring_task', role: 'filter' },
        choices: [
            { choice_key: 'no_deleted', choice_name: '' },
            { choice_key: 'show_deleted', choice_name: '' },
        ],
    },
    // Keep the nudge dismissed state explicit so walkthrough mode mirrors a real settings payload.
    {
        field_key: 'has_dismissed_goal_nudge',
        field_value: 'false',
        field_name: 'has_dismissed_goal_nudge',
        choices: [],
    },
]

// ScheduleTaskModal disables its submit button until the calendar list resolves, so the account has
// to carry at least one writable calendar for the modal to be in its real, usable state.
export const calendars: TCalendarAccount[] = [
    {
        account_id: CALENDAR_ACCOUNT_ID,
        has_multical_scopes: true,
        has_primary_calendar_scopes: true,
        calendars: [
            {
                calendar_id: CALENDAR_ACCOUNT_ID,
                color_id: '',
                title: 'Walkthrough',
                can_write: true,
                access_role: 'owner',
                color_background: '#ffffff',
                color_foreground: '#000000',
            },
        ],
    },
]

// A paced goal with one linked task, so walkthroughs can show the goal loop: completing the task
// saves a contribution and moves the This week meter. Dates are relative so the fixture never ages.
export const GOAL_ID = '5f9b3b3b3b3b3b3b3b3b3b10'
export const GOAL_TASK_ID = '5f9b3b3b3b3b3b3b3b3b3b11'
export const SECOND_GOAL_ID = '5f9b3b3b3b3b3b3b3b3b3b12'
export const RECURRING_TEMPLATE_ID = '5f9b3b3b3b3b3b3b3b3b3b13'
export const SECOND_RECURRING_TEMPLATE_ID = '5f9b3b3b3b3b3b3b3b3b3b14'
export const GOAL_TITLE = 'Run three times a week'

const weekStart = DateTime.local().startOf('week')

export const goalTask: TTaskV4 = {
    ...task,
    id: GOAL_TASK_ID,
    id_ordering: 2,
    title: 'Easy 5k loop',
    body: '',
    id_folder: undefined,
    recurring_task_template_id: RECURRING_TEMPLATE_ID,
}

// Two hooked repeats, one holding time and one not, so a walkthrough can show both rows of the
// Repeats section: a solid week ribbon with a held total, and a dashed tick reading "not held".
export const recurringTemplate: TRecurringTaskTemplate = {
    id: RECURRING_TEMPLATE_ID,
    title: 'Easy 5k loop',
    id_task_section: FOLDER_ID,
    recurrence_rate: 2,
    time_of_day_seconds_to_create_task: 7 * 60 * 60,
    block_duration_minutes: 45,
    day_to_create_task: 1,
    last_backfill_datetime: weekStart.minus({ weeks: 1 }).toISO(),
    created_at: weekStart.minus({ weeks: 4 }).toISO(),
    updated_at: weekStart.minus({ weeks: 1 }).toISO(),
    is_deleted: false,
    is_enabled: true,
}

export const secondRecurringTemplate: TRecurringTaskTemplate = {
    ...recurringTemplate,
    id: SECOND_RECURRING_TEMPLATE_ID,
    title: 'Strength session',
    time_of_day_seconds_to_create_task: 0,
    block_duration_minutes: undefined,
    day_to_create_task: 4,
}

export const goal: TGoal = {
    id: GOAL_ID,
    title: GOAL_TITLE,
    why: 'I want to feel strong on the trail again by spring.',
    timeframeLabel: 'Next 90 days',
    targetLabel: 'Self-graded',
    paceLabel: '',
    progress: 0.2,
    progressLabel: '3 of 12 sessions',
    status: 'on_track',
    progressMode: 'cadence',
    goalType: 'consistency',
    contractLine: 'Counts as off track after 1 quiet week.',
    weekLabel: '1 of 1 this week',
    startDate: weekStart.minus({ weeks: 2 }).toISODate(),
    weeks: ['complete', 'complete', 'complete'],
    endDate: weekStart.plus({ weeks: 9 }).toISODate(),
    weeklyTarget: 1,
    thisWeek: 1,
    recent: [
        {
            id: 'log-walkthrough-1',
            title: 'Hill repeats',
            source: 'Logged',
            at: weekStart.plus({ hours: 8 }).toISO(),
            amount: 1,
        },
    ],
    // What the server sends alongside thisWeek: one entry per filled unit of the meter, oldest
    // first, so each segment can say what put it there. Kept in step with `recent` by hand here;
    // syncWalkthroughGoals recomputes both together once a recording starts completing tasks.
    thisWeekUnits: [
        {
            id: 'log-walkthrough-1',
            title: 'Hill repeats',
            source: 'Logged',
            at: weekStart.plus({ hours: 8 }).toISO(),
            amount: 1,
        },
    ],
}

export const goalRedraftPaths: TScenarioPath[] = [
    {
        id: 'trail-rhythm',
        type: 'consistency',
        label: 'Trail rhythm',
        shapeLabel: '2 focused runs / week',
        rationale: 'A lighter cadence keeps the spring goal moving.',
        phases: [
            {
                name: 'Reset',
                cadenceLabel: '2 runs / week',
                weeklyHours: 2,
                dateSpanLabel: 'Next 30 days',
                weeks: 4,
            },
            {
                name: 'Build',
                cadenceLabel: '3 runs / week',
                weeklyHours: 3,
                dateSpanLabel: 'After reset',
                weeks: 6,
            },
        ],
        items: [
            { id: 'easy-run', kind: 'cadence', title: 'Easy trail run', frequencyLabel: '2× / week', included: true },
            {
                id: 'book-race',
                kind: 'cadence',
                title: 'Book the spring race',
                frequencyLabel: '1× / month',
                included: true,
            },
            { id: 'long-run', kind: 'milestone', title: 'Complete a steady long run', included: true },
        ],
    },
]

/**
 * What the planner returns once the user's feedback lands: the booking is a one-off, which
 * drops it out of the weekly target it never could have met. Serving this only when a
 * request carries feedback is what makes the revision visible in a walkthrough.
 */
export const goalRedraftRevisedPaths: TScenarioPath[] = [
    {
        ...goalRedraftPaths[0],
        rationale: 'The booking happens once, so only the runs set the weekly pace.',
        items: [
            { id: 'easy-run', kind: 'cadence', title: 'Easy trail run', frequencyLabel: '2× / week', included: true },
            { id: 'book-race', kind: 'oneoff', title: 'Book the spring race', included: true },
            { id: 'long-run', kind: 'milestone', title: 'Complete a steady long run', included: true },
        ],
    },
]

const iso = (dateTime: DateTime) => dateTime.toISO() ?? ''

export const internalDashboardSummary: TInternalDashboardSummary = {
    snapshot_at: iso(DateTime.local()),
    counts: {
        total_users: 18,
        signups_last_7_days: 6,
        signups_last_30_days: 14,
        activated_users: 9,
        activated_last_7_days: 4,
        paid_users: 3,
        trialing_users: 5,
    },
    recent_users: [
        {
            email: 'alex@example.com',
            name: 'Alex Chen',
            created_at: iso(DateTime.local().minus({ days: 1 })),
            trial_ends_at: iso(DateTime.local().plus({ days: 13 })),
            subscription_status: '',
            is_subscribed: false,
            is_in_trial: true,
            trial_days_remaining: 13,
            has_product_access: true,
            task_count: 8,
            completed_task_count: 3,
            last_completed_at: iso(DateTime.local().minus({ hours: 2 })),
            activated: true,
            email_unsubscribed: false,
            welcome_email_sent_at: iso(DateTime.local().minus({ days: 1 })),
            subscription_acknowledgment: '',
        },
        {
            email: 'maya@example.com',
            name: 'Maya Patel',
            created_at: iso(DateTime.local().minus({ days: 2 })),
            trial_ends_at: iso(DateTime.local()),
            subscription_status: 'active',
            is_subscribed: true,
            is_in_trial: false,
            trial_days_remaining: 0,
            has_product_access: true,
            task_count: 12,
            completed_task_count: 5,
            last_completed_at: iso(DateTime.local().minus({ hours: 5 })),
            activated: true,
            email_unsubscribed: true,
            welcome_email_sent_at: iso(DateTime.local().minus({ days: 2 })),
            subscription_acknowledgment: 'sent',
        },
        {
            email: 'sam@example.com',
            name: 'Sam Rivera',
            created_at: iso(DateTime.local().minus({ days: 4 })),
            trial_ends_at: iso(DateTime.local()),
            subscription_status: 'trialing',
            is_subscribed: true,
            is_in_trial: false,
            trial_days_remaining: 0,
            has_product_access: true,
            task_count: 5,
            completed_task_count: 0,
            last_completed_at: '',
            activated: false,
            email_unsubscribed: false,
            welcome_email_sent_at: '',
            subscription_acknowledgment: 'pending',
        },
    ],
}

const usageWindowDays: Record<TInternalUsageWindow, number> = { '7d': 7, '30d': 30, '90d': 90 }

// Goals & Routines usage for the internal dashboard. Wider windows add created goals and routine
// days so switching the window visibly changes the figures.
export const internalUsage = (window: TInternalUsageWindow): TInternalUsage => {
    const days = usageWindowDays[window]
    const scale = days / 7
    return {
        snapshot_at: iso(DateTime.local()),
        window,
        window_start: iso(DateTime.local().minus({ days })),
        goals: {
            users: 4,
            total: 9,
            created_in_window: Math.min(9, Math.round(2 * scale)),
            active: 6,
            completed: 2,
            archived: 1,
            active_with_linked_tasks: 4,
            median_linked_tasks_per_goal: 1.5,
        },
        routines: {
            users: 3,
            total: 4,
            created_in_window: window === '7d' ? 1 : 3,
            days_completed: Math.round((4 * days - 4) * 0.58),
            without_completion_in_window: window === '7d' ? 1 : 0,
        },
        users: [
            {
                email: 'maya@example.com',
                name: 'Maya Patel',
                subscription_status: 'active',
                is_in_trial: false,
                trial_days_remaining: 0,
                trial_ends_at: iso(DateTime.local()),
                goal_count: 3,
                routine_count: 2,
                last_activity_at: iso(DateTime.local().minus({ hours: 3 })),
            },
            {
                email: 'alex@example.com',
                name: 'Alex Chen',
                subscription_status: '',
                is_in_trial: true,
                trial_days_remaining: 13,
                trial_ends_at: iso(DateTime.local().plus({ days: 13 })),
                goal_count: 2,
                routine_count: 1,
                last_activity_at: iso(DateTime.local().minus({ days: 1 })),
            },
            {
                email: 'sam@example.com',
                name: 'Sam Rivera',
                subscription_status: 'trialing',
                is_in_trial: false,
                trial_days_remaining: 0,
                trial_ends_at: iso(DateTime.local()),
                goal_count: 1,
                routine_count: 0,
                last_activity_at: iso(DateTime.local().minus({ days: 6 })),
            },
            {
                email: 'john@johnglomerate.com',
                name: 'John',
                subscription_status: 'active',
                is_in_trial: false,
                trial_days_remaining: 0,
                trial_ends_at: '',
                goal_count: 3,
                routine_count: 1,
                last_activity_at: iso(DateTime.local().minus({ days: 12 })),
            },
        ],
    }
}

// An hours-unit goal, so the pane can show the read that only a held repeat can satisfy: time held
// per week against an hours target, summed from the block length on each hooked template.
export const secondGoal: TGoal = {
    ...goal,
    id: SECOND_GOAL_ID,
    title: 'Build trail stamina',
    why: 'Stay ready for weekend climbs.',
    goalType: 'time',
    unit: 'hrs',
    progressLabel: '6 of 48 hrs',
    weekLabel: '0 of 4 this week',
    weeklyTarget: 4,
    thisWeek: 0,
    thisWeekUnits: [],
    recent: [],
}
